import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  DEFAULT_BODY_FONT_SIZE,
  DEFAULT_BODY_FONT_WEIGHT,
  DEFAULT_BODY_LETTER_SPACING,
  DEFAULT_COPY_FONT_ID,
  DEFAULT_TITLE_LETTER_SPACING,
  MAX_BODY_FONT_SIZE,
  MAX_BODY_FONT_WEIGHT,
  MAX_BODY_LETTER_SPACING,
  MAX_TITLE_LETTER_SPACING,
  MIN_BODY_FONT_SIZE,
  MIN_BODY_FONT_WEIGHT,
  MIN_BODY_LETTER_SPACING,
  MIN_TITLE_LETTER_SPACING,
  TYPOGRAPHY_PRESETS,
  findFont,
} from './typography.system';
import { useTypography } from './TypographyProvider';

export function TypographyTweakPanel() {
  const { search } = useLocation();
  const { settings, updateSettings, applyPreset, resetTypography } = useTypography();
  const shouldShowPanel = !import.meta.env.VITE_E2E_AUTH_BYPASS
    && new URLSearchParams(search).get('tweak') === 'true';
  const [isOpen, setIsOpen] = useState(shouldShowPanel);

  useEffect(() => {
    if (shouldShowPanel) {
      setIsOpen(true);
    }
  }, [shouldShowPanel]);

  const selectedPreset = TYPOGRAPHY_PRESETS.find((preset) => preset.id === settings.presetId);
  const copyFont = findFont(DEFAULT_COPY_FONT_ID);
  const isCopyFontActive = settings.bodyFontId === DEFAULT_COPY_FONT_ID && settings.buttonFontId === DEFAULT_COPY_FONT_ID;

  if (!shouldShowPanel) {
    return null;
  }

  return (
    <aside className="fixed bottom-4 right-4 z-[80] print:hidden">
      {isOpen ? (
        <div className="w-[min(360px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-xl border border-line bg-panel shadow-xl">
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-line bg-panel px-4 py-3">
            <div>
              <div className="type-kicker text-text-faint">Typography</div>
              <h2 className="type-title-card mt-1">Tweak panel</h2>
            </div>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setIsOpen(false)}
            >
              Close
            </button>
          </div>

          <div className="space-y-5 p-4">
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="type-title-card text-[18px]">Presets</h3>
                <button type="button" className="type-label-sm text-text-dim hover:text-text" onClick={resetTypography}>
                  Reset
                </button>
              </div>
              <div className="grid gap-2">
                {TYPOGRAPHY_PRESETS.map((preset) => (
                  <button
                    type="button"
                    key={preset.id}
                    onClick={() => applyPreset(preset.id)}
                    className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                      settings.presetId === preset.id
                        ? 'border-text bg-bg-secondary text-text'
                        : 'border-line bg-panel text-text-dim hover:border-text/35 hover:text-text'
                    }`}
                  >
                    <span className="type-label block">{preset.label}</span>
                    <span className="type-caption mt-0.5 block text-text-dim">{preset.description}</span>
                  </button>
                ))}
              </div>
            </section>

            <section className="grid gap-3">
              <div>
                <h3 className="type-title-card text-[18px]">Fraunces spacing</h3>
                <p className="type-caption mt-1 text-text-dim">Adjusts every title that uses the Fraunces title style.</p>
              </div>
              <label className="grid gap-2 rounded-lg border border-line-soft bg-bg-secondary p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="type-label-sm text-text-dim">Letter spacing</span>
                  <span className="type-code text-text">{settings.titleLetterSpacing.toFixed(3)}em</span>
                </div>
                <input
                  type="range"
                  min={MIN_TITLE_LETTER_SPACING}
                  max={MAX_TITLE_LETTER_SPACING}
                  step={0.001}
                  value={settings.titleLetterSpacing}
                  onChange={(event) => updateSettings({ titleLetterSpacing: Number(event.target.value) })}
                  className="w-full accent-text"
                />
                <div className="flex items-center justify-between">
                  <span className="type-caption text-text-faint">Tighter</span>
                  <button
                    type="button"
                    className="type-label-sm text-text-dim hover:text-text"
                    onClick={() => updateSettings({ titleLetterSpacing: DEFAULT_TITLE_LETTER_SPACING })}
                  >
                    Default
                  </button>
                  <span className="type-caption text-text-faint">Looser</span>
                </div>
              </label>
            </section>

            <section className="grid gap-3">
              <div>
                <h3 className="type-title-card text-[18px]">Main copy</h3>
                <p className="type-caption mt-1 text-text-dim">Literata drives regular text, forms, labels, and buttons.</p>
              </div>
              <div className="rounded-lg border border-line-soft bg-bg-secondary p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <span className="type-label-sm text-text-dim">Copy font</span>
                    <p className="type-body mt-1 text-text" style={{ fontFamily: copyFont.stack }}>{copyFont.label}</p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={isCopyFontActive}
                    onClick={() => updateSettings({ bodyFontId: DEFAULT_COPY_FONT_ID, buttonFontId: DEFAULT_COPY_FONT_ID })}
                  >
                    {isCopyFontActive ? 'Active' : 'Use Literata'}
                  </button>
                </div>
                <p className="type-caption mt-2 text-text-faint">{copyFont.note}</p>
              </div>
              <RangeControl
                label="Letter spacing"
                value={settings.bodyLetterSpacing}
                min={MIN_BODY_LETTER_SPACING}
                max={MAX_BODY_LETTER_SPACING}
                step={0.001}
                displayValue={`${settings.bodyLetterSpacing.toFixed(3)}em`}
                lowLabel="Tighter"
                highLabel="Looser"
                onChange={(bodyLetterSpacing) => updateSettings({ bodyLetterSpacing })}
                onReset={() => updateSettings({ bodyLetterSpacing: DEFAULT_BODY_LETTER_SPACING })}
              />
              <RangeControl
                label="Font weight"
                value={settings.bodyFontWeight}
                min={MIN_BODY_FONT_WEIGHT}
                max={MAX_BODY_FONT_WEIGHT}
                step={50}
                displayValue={String(settings.bodyFontWeight)}
                lowLabel="Lighter"
                highLabel="Heavier"
                onChange={(bodyFontWeight) => updateSettings({ bodyFontWeight })}
                onReset={() => updateSettings({ bodyFontWeight: DEFAULT_BODY_FONT_WEIGHT })}
              />
              <RangeControl
                label="Size"
                value={settings.bodyFontSize}
                min={MIN_BODY_FONT_SIZE}
                max={MAX_BODY_FONT_SIZE}
                step={0.5}
                displayValue={`${settings.bodyFontSize}px`}
                lowLabel="Smaller"
                highLabel="Larger"
                onChange={(bodyFontSize) => updateSettings({ bodyFontSize })}
                onReset={() => updateSettings({ bodyFontSize: DEFAULT_BODY_FONT_SIZE })}
              />
            </section>

            <section className="rounded-lg border border-line-soft bg-bg-secondary p-3">
              <div className="type-kicker text-text-faint">Active mix</div>
              <p className="mt-2 type-body-sm">
                {selectedPreset?.label || 'Custom'}: Fraunces 300 titles,
                {' '}{settings.titleLetterSpacing.toFixed(3)}em title spacing,
                {' '}{findFont(settings.bodyFontId).label} copy at {settings.bodyFontSize}px,
                {' '}weight {settings.bodyFontWeight}, {settings.bodyLetterSpacing.toFixed(3)}em copy spacing.
              </p>
              <Link to="/typography" className="type-label-sm mt-3 inline-flex text-text underline decoration-line underline-offset-4 hover:decoration-text">
                Open the full audit
              </Link>
            </section>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-primary shadow-lg"
          onClick={() => setIsOpen(true)}
          aria-label="Open typography tweak panel"
        >
          Type
        </button>
      )}
    </aside>
  );
}

function RangeControl({
  label,
  value,
  min,
  max,
  step,
  displayValue,
  lowLabel,
  highLabel,
  onChange,
  onReset,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  displayValue: string;
  lowLabel: string;
  highLabel: string;
  onChange: (value: number) => void;
  onReset: () => void;
}) {
  return (
    <label className="grid gap-2 rounded-lg border border-line-soft bg-bg-secondary p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="type-label-sm text-text-dim">{label}</span>
        <span className="type-code text-text">{displayValue}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="w-full accent-text"
      />
      <div className="flex items-center justify-between">
        <span className="type-caption text-text-faint">{lowLabel}</span>
        <button
          type="button"
          className="type-label-sm text-text-dim hover:text-text"
          onClick={onReset}
        >
          Default
        </button>
        <span className="type-caption text-text-faint">{highLabel}</span>
      </div>
    </label>
  );
}
